import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { useForm, Controller } from 'react-hook-form';
import { Task } from '@tassomutuo-backoffice/types';

interface EditTaskModalProps {
  open: boolean;
  task: Task;
  onClose: () => void;
  onSubmit: (data: { action: string; notes?: string; date: string }) => void;
  isLoading?: boolean;
}

interface TaskFormData {
  date: Date;
  action: string;
  notes: string;
}

const EditTaskModal: React.FC<EditTaskModalProps> = ({
  open,
  task,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TaskFormData>({
    defaultValues: {
      date: new Date(task.date),
      action: task.action,
      notes: task.notes || '',
    },
  });

  // Update form when task changes
  useEffect(() => {
    if (open) {
      reset({
        date: new Date(task.date),
        action: task.action,
        notes: task.notes || '',
      });
    }
  }, [open, task, reset]);

  const handleClose = () => {
    if (!isLoading) {
      reset();
      onClose();
    }
  };

  const onFormSubmit = (data: TaskFormData) => {
    onSubmit({
      date: data.date.toISOString(),
      action: data.action.trim(),
      notes: data.notes.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Modifica promemoria</DialogTitle>
      <DialogContent>
        <Box
          id="edit-task-form"
          component="form"
          onSubmit={handleSubmit(onFormSubmit)}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
            <Controller
              name="date"
              control={control}
              rules={{
                required: 'Data e ora sono obbligatorie',
                validate: (value) => {
                  if (!value) return 'Data e ora sono obbligatorie';
                  return true;
                },
              }}
              render={({ field }) => (
                <DateTimePicker
                  label="Data e Ora"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isLoading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!errors.date,
                      helperText: errors.date?.message,
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>

          <Controller
            name="action"
            control={control}
            rules={{
              required: 'Azione è obbligatoria',
              minLength: {
                value: 3,
                message: "L'azione deve contenere almeno 3 caratteri",
              },
              maxLength: {
                value: 500,
                message: "L'azione non può superare 500 caratteri",
              },
              validate: (value) =>
                value.trim().length >= 3 ||
                "L'azione deve contenere almeno 3 caratteri",
            }}
            render={({ field }) => (
              <TextField
                label="Azione"
                {...field}
                error={!!errors.action}
                helperText={errors.action?.message}
                fullWidth
                required
                disabled={isLoading}
                multiline
                rows={2}
              />
            )}
          />

          <Controller
            name="notes"
            control={control}
            rules={{
              maxLength: {
                value: 1000,
                message: 'Le note non possono superare 1000 caratteri',
              },
            }}
            render={({ field }) => (
              <TextField
                label="Note"
                {...field}
                error={!!errors.notes}
                helperText={errors.notes?.message}
                fullWidth
                disabled={isLoading}
                multiline
                rows={3}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Annulla
        </Button>
        <Button
          type="submit"
          form="edit-task-form"
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Salvataggio...' : 'Salva Modifiche'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTaskModal;
